import { HttpClient, PaginatedResponse, robotoHeaders } from "@/shared/http";

import {
  CreateCustomFieldRequest,
  CustomFieldRecord,
  ListCustomFieldsRequest,
  UpdateCustomFieldRequest,
} from "./CustomFieldRecord";

interface Options {
  abortSignal: AbortSignal;
  orgId: string;
}

/**
 * Frontend mirror of the Python `roboto.domain.custom_fields` SDK.
 *
 * Routes wired to
 * `packages/roboto_service/src/roboto_service/routes/custom_fields_router.py`:
 *   - `POST v1/custom-fields/query` → listCustomFields (paginated)
 *   - `POST v1/custom-fields` → createCustomField (org-admin only)
 *   - `POST v1/custom-fields/id/<field_id>` → updateCustomField (org-admin only)
 *   - `DELETE v1/custom-fields/id/<field_id>` → deleteCustomField (org-admin only)
 */
export class CustomFieldService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async listCustomFields(
    request: ListCustomFieldsRequest,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<CustomFieldRecord>> {
    const requestUrl = this.#httpClient.constructUrl(`v1/custom-fields/query`);

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return response.json<PaginatedResponse<CustomFieldRecord>>();
  }

  public async createCustomField(
    request: CreateCustomFieldRequest,
    options?: Partial<Options>,
  ): Promise<CustomFieldRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/custom-fields`);

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return response.json<CustomFieldRecord>();
  }

  public async updateCustomField(
    fieldId: string,
    request: UpdateCustomFieldRequest,
    options?: Partial<Options>,
  ): Promise<CustomFieldRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/custom-fields/id/${fieldId}`,
    );

    // Authorizes against the field's owning org (the SDK's `owner_org_id`).
    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return response.json<CustomFieldRecord>();
  }

  public async deleteCustomField(
    fieldId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/custom-fields/id/${fieldId}`,
    );

    // Authorizes against the field's owning org (the SDK's `owner_org_id`).
    const response = await this.#httpClient.delete(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    await response.throwIfError();
  }
}
